import { AppCheckbox, AppTable, AppTooltip } from '@/components/atoms';
import type { RezeptListItem } from '@/types/rezept.types';
import { useTheme } from '@shared/providers/theme/ThemeContext';
import type { ColumnsType } from 'antd/es/table';
import type { TableRowSelection } from 'antd/es/table/interface';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { TABLE_SCROLL_Y } from '../constants';
import {
  formatLateReturnBadge,
  formatRezeptKbn,
  formatStatus,
  formatTreatementYearmonth,
} from '../utils';

interface Props {
  rows: RezeptListItem[];
  isLoading: boolean;
  selectedIds: Set<number>;
  duplicateTreatmentReportFeeIds: Set<number>;
  hasFilters: boolean;
  onSelectionChange: (ids: number[]) => void;
  onDoubleClickRow: (row: RezeptListItem) => void;
  onToggleTreatmentReport: (id: number, value: 0 | 1) => void;
}

const ROW_CLICK_DELAY_MS = 220;
const INTERACTIVE_ROW_TARGET_SELECTOR = [
  'a',
  'button',
  'input',
  'label',
  'textarea',
  'select',
  '[role="button"]',
  '[data-row-action="true"]',
  '.ant-checkbox-wrapper',
  '.ant-table-selection-column',
].join(', ');

const shouldCellUpdate = (record: RezeptListItem, prevRecord: RezeptListItem) =>
  record !== prevRecord;

const isInteractiveRowTarget = (target: EventTarget | null) =>
  target instanceof Element && !!target.closest(INTERACTIVE_ROW_TARGET_SELECTOR);

const RezeptListTable: React.FC<Props> = ({
  rows,
  isLoading,
  selectedIds,
  duplicateTreatmentReportFeeIds,
  hasFilters,
  onSelectionChange,
  onDoubleClickRow,
  onToggleTreatmentReport,
}) => {
  const navigate = useNavigate();
  const { siteType } = useTheme();
  const selectedIdsRef = useRef(selectedIds);
  const rowClickTimersRef = useRef<Map<number, number>>(new Map());

  useEffect(() => {
    selectedIdsRef.current = selectedIds;
  }, [selectedIds]);

  // BD: clicking "未" → opens the create-report screen "with the month pre-filled" → pass ym via query
  // so CreateTreatmentReportPage pre-fills treatment_yearmonth.
  const toCreate = useCallback(
    (patientId: number, ym?: string) =>
      `/${siteType}/treatment-reports/${patientId}/create${ym ? `?ym=${ym}` : ''}`,
    [siteType]
  );
  const toEdit = useCallback(
    (patientId: number, reportId: number) =>
      `/${siteType}/treatment-reports/${patientId}/edit/${reportId}`,
    [siteType]
  );

  const selectedRowKeys = useMemo(() => Array.from(selectedIds), [selectedIds]);

  const toggleSelectedRow = useCallback(
    (id: number) => {
      const next = new Set(selectedIdsRef.current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      onSelectionChange(Array.from(next));
    },
    [onSelectionChange]
  );

  const clearRowClickTimer = useCallback((id: number) => {
    const timer = rowClickTimersRef.current.get(id);
    if (timer) {
      window.clearTimeout(timer);
      rowClickTimersRef.current.delete(id);
    }
  }, []);

  useEffect(
    () => () => {
      rowClickTimersRef.current.forEach(timer => window.clearTimeout(timer));
      rowClickTimersRef.current.clear();
    },
    []
  );

  const handleRowClick = useCallback(
    (row: RezeptListItem, event: React.MouseEvent<HTMLElement>) => {
      if (isInteractiveRowTarget(event.target)) return;

      if (event.detail > 1) {
        clearRowClickTimer(row.id);
        return;
      }

      clearRowClickTimer(row.id);
      const timer = window.setTimeout(() => {
        toggleSelectedRow(row.id);
        rowClickTimersRef.current.delete(row.id);
      }, ROW_CLICK_DELAY_MS);
      rowClickTimersRef.current.set(row.id, timer);
    },
    [clearRowClickTimer, toggleSelectedRow]
  );

  const handleRowDoubleClick = useCallback(
    (row: RezeptListItem, event: React.MouseEvent<HTMLElement>) => {
      if (isInteractiveRowTarget(event.target)) return;
      clearRowClickTimer(row.id);
      onDoubleClickRow(row);
    },
    [clearRowClickTimer, onDoubleClickRow]
  );

  const rowSelection = useMemo<TableRowSelection<RezeptListItem>>(
    () => ({
      selectedRowKeys,
      onChange: keys => onSelectionChange(keys.map(Number)),
      columnWidth: 56,
      align: 'center',
      getTitleCheckboxProps: () => ({ 'aria-label': '全選択' }),
      renderCell: (_checked, _record, _index, originNode) => (
        <span onClick={e => e.stopPropagation()} onDoubleClick={e => e.stopPropagation()}>
          {originNode}
        </span>
      ),
    }),
    [onSelectionChange, selectedRowKeys]
  );

  const columns: ColumnsType<RezeptListItem> = useMemo(() => {
    const baseColumns: ColumnsType<RezeptListItem> = [
      {
        title: '番号',
        dataIndex: 'serial_number',
        key: 'serial_number',
        width: 56,
      },
      {
        title: '受診者名',
        dataIndex: 'patient_name',
        key: 'patient_name',
        width: 170,
      },
      {
        title: '保険者名',
        dataIndex: 'insurer_name',
        key: 'insurer_name',
        width: 150,
      },
      {
        title: '施術年月',
        dataIndex: 'treatement_yearmonth',
        key: 'treatement_yearmonth',
        width: 120,
        render: value => formatTreatementYearmonth(value),
      },
      {
        title: '本・関',
        dataIndex: 'rezept_kbn',
        key: 'rezept_kbn',
        width: 56,
        align: 'center',
        render: (value, row) => formatRezeptKbn(value, row.nonoutput_rezept_medical_aid_number),
      },
      {
        title: '遅・返',
        key: 'late_return',
        width: 56,
        align: 'center',
        render: (_, row) => formatLateReturnBadge(row),
      },
      {
        title: '用紙',
        dataIndex: 'rezept_template_name',
        key: 'rezept_template_name',
        width: 64,
      },
      {
        title: '同意連番',
        dataIndex: 'consent_serial_number',
        key: 'consent_serial_number',
        width: 70,
      },
      {
        title: '報告料',
        key: 'add_treatment_report',
        width: 56,
        align: 'center',
        render: (_, row) => {
          const hasReport = !!row.is_treatment_report || row.is_treatment_report2 === 2;
          if (!hasReport) return null;
          const locked = row.status === 1 || row.status === 2 || !!row.is_treatment_report_default;
          const checked = row.add_treatment_report === 1;
          return (
            <AppCheckbox
              checked={checked}
              disabled={locked}
              onChange={e => {
                if (!locked) onToggleTreatmentReport(row.id, e.target.checked ? 1 : 0);
              }}
              onClick={e => e.stopPropagation()}
            />
          );
        },
      },
      {
        title: '報告書',
        key: 'report_link',
        // String width so AppTable's >=80 number-width auto-distribute is skipped —
        // keeps this sparse column compact instead of inflating to an equal share.
        width: '80px',
        render: (_, row) => {
          const linkStyle = 'text-blue-600 underline cursor-pointer text-sm';

          // Report 1
          let report1: React.ReactNode = null;
          if (row.is_treatment_report && row.treatment_report_id) {
            const isManual = row.first_report_maker === '手動';
            report1 = (
              <span
                className={linkStyle}
                data-row-action="true"
                onClick={e => {
                  e.stopPropagation();
                  navigate(toEdit(row.patient_id, row.treatment_report_id!));
                }}
                onDoubleClick={e => e.stopPropagation()}
              >
                作成済{isManual && <span className="text-red-500">●</span>}
              </span>
            );
          } else if (row.is_treatment_report2 === 2) {
            // Pass treatement_report_month (the month the system determined needs reporting)
            // — not treatement_yearmonth, because when consent.orthomorphia=null
            // report_month can differ (= consent.term_to month).
            const reportYm = row.treatement_report_month || row.treatement_yearmonth;
            report1 = (
              <span
                className={linkStyle}
                data-row-action="true"
                onClick={e => {
                  e.stopPropagation();
                  navigate(toCreate(row.patient_id, reportYm));
                }}
                onDoubleClick={e => e.stopPropagation()}
              >
                未
              </span>
            );
          }

          // Report 2 (only when second_report=true AND second_report_id != 0)
          let report2: React.ReactNode = null;
          if (row.second_report && row.second_report_id) {
            const isManual2 = row.second_report_maker === '手動';
            report2 = (
              <span
                className={linkStyle}
                data-row-action="true"
                onClick={e => {
                  e.stopPropagation();
                  navigate(toEdit(row.patient_id, row.second_report_id!));
                }}
                onDoubleClick={e => e.stopPropagation()}
              >
                作成済{isManual2 && <span className="text-red-500">●</span>}
              </span>
            );
          }

          if (!report1 && !report2) return null;
          return (
            <div className="flex flex-col gap-0.5">
              {report1}
              {report2}
            </div>
          );
        },
      },
      {
        title: '助成期限',
        key: 'application_end_date',
        // String width that fits the date "2026/03/31" and skips AppTable's >=80
        // number-width auto-distribute (which would inflate this sparse column).
        width: '88px',
        render: (_, row) => {
          if (!row.is_ng_medical_aid) return null;
          if (!row.nonoutput_rezept_medical_aid_number && row.rezept_kbn !== 2) return null;
          if (!row.application_end_date) return null;
          return (
            <span className="text-red-500">{row.application_end_date.replace(/-/g, '/')}</span>
          );
        },
      },
      {
        title: '状況',
        dataIndex: 'status',
        key: 'status',
        width: 60,
        align: 'center',
        render: value => formatStatus(value),
      },
      {
        title: '施術管理者',
        dataIndex: 'manager_name',
        key: 'manager_name',
        width: 130,
        render: (_, row) => {
          if (!row.rezept_template_value || !/M_J4/.test(row.rezept_template_value)) {
            return null;
          }
          return row.manager_name ? row.manager_name : '登録なし';
        },
      },
      {
        title: '施術師名',
        dataIndex: 'all_massager_names',
        key: 'massager_name',
        width: 140,
        ellipsis: true,
        render: (_: unknown, row: RezeptListItem) => {
          const names = row.all_massager_names || row.massager_name || '';
          return (
            <AppTooltip title={names} showOnlyOnOverflow>
              <div className="max-w-full min-w-0 overflow-hidden break-all truncate line-clamp-1">
                {names}
              </div>
            </AppTooltip>
          );
        },
      },
    ];

    // Center every column header (body alignment stays per-column: only
    // 本・関 / 遅・返 / 状況 set align:'center' for their cells).
    return baseColumns.map(column => ({
      ...column,
      shouldCellUpdate,
      onHeaderCell: () => ({ style: { textAlign: 'center' as const } }),
    }));
  }, [navigate, onToggleTreatmentReport, toCreate, toEdit]);

  return (
    <AppTable<RezeptListItem>
      className="rezept-list-table"
      rowKey="id"
      columns={columns}
      dataSource={rows}
      loading={isLoading}
      pagination={false}
      hasFilters={hasFilters}
      // Drop horizontal scroll — fit container; overly long cells → ellipsis + tooltip on hover.
      scroll={{ y: TABLE_SCROLL_Y }}
      tableLayout="fixed"
      enableEllipsis
      rowSelection={rowSelection}
      rowClassName={row => {
        if (duplicateTreatmentReportFeeIds.has(row.id)) return 'duplicate-treatment-report-fee-row';
        if (selectedIds.has(row.id)) return 'bg-sky-200';
        return '';
      }}
      onRow={row => ({
        onClick: event => handleRowClick(row, event),
        onDoubleClick: event => handleRowDoubleClick(row, event),
        style: { cursor: 'pointer' },
      })}
    />
  );
};

export default RezeptListTable;
